import { useContext, useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  House,
  ShoppingBag,
  Compass,
  UserRound,
  Package,
  ShieldCheck,
  LayoutDashboard,
  LogOut,
  LogIn,
  UserPlus,
  ChevronDown,
  ChevronRight,
  Menu,
  Sparkles,
  Sun,
  Moon,
  UserCog,
} from "lucide-react";
import { T } from "../constants/theme";
import { API } from "../constants/config";
import { CartContext } from "../contexts/CartContext";
import cropbankLogo from "../assets/cropbank-logo.png";
import { AuthContext } from "../contexts/AuthContext";
import { ThemeContext } from "../contexts/ThemeContext";
import { useApi } from "../hooks/useApi";
import { normalizeCategories } from "../utils/categoryHelpers";

export default function Navbar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setPage = (r) => navigate(`/${r}`);
  const { setOpen, count } = useContext(CartContext);
  const { user, logout } = useContext(AuthContext);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [themeHover, setThemeHover] = useState(false);
  const { data: categoriesData } = useApi("/categories");
  const categories = useMemo(
    () => normalizeCategories(categoriesData),
    [categoriesData]
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 900 : false,
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuHover, setMenuHover] = useState(false);
  const [activeMenuSlug, setActiveMenuSlug] = useState("");
  const [categoryImageMap, setCategoryImageMap] = useState({});
  const [hoveredNav, setHoveredNav] = useState("");
  const [categoryBtnHover, setCategoryBtnHover] = useState(false);
  const [cartHover, setCartHover] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!categories.length) return;

    let isMounted = true;

    const fetchImages = async () => {
      const results = await Promise.all(
        categories.map(async (cat) => {
          try {
            const res = await fetch(
              `${API}/images/category/${cat.id}`
            );
            if (!res.ok) return [cat.id, null];
            const data = await res.json();
            return [cat.id, data.url || null];
          } catch {
            return [cat.id, null];
          }
        })
      );

      if (isMounted) {
        setCategoryImageMap(Object.fromEntries(results));
      }
    };

    fetchImages();

    return () => {
      isMounted = false;
    };
  }, [categories]);

  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);

  const currentCategory = searchParams.get("cat") || "";

  const goCategory = (slug = "") => {
    setPage(slug ? `shop?cat=${slug}` : "shop");
    setMenuOpen(false);
  };

  useEffect(() => {
    if (currentCategory) {
      setActiveMenuSlug(currentCategory);
      return;
    }
    if (!activeMenuSlug && categories.length) {
      setActiveMenuSlug(categories[0].slug || "");
    }
  }, [currentCategory, categories, activeMenuSlug]);

  const showCategoryMenu = isMobile ? menuOpen : menuOpen || menuHover;
  const activeCategory =
    categories.find((c) => c.slug === activeMenuSlug) || categories[0] || null;
  const activeCategoryImage = activeCategory
    ? categoryImageMap[activeCategory.id]
    : null;
  const menuFeatureGroups = [
    {
      title: "Projects",
      links: [
        {
          label: activeCategory?.name || "Featured Projects",
          action: () => goCategory(activeCategory?.slug || ""),
        },
        {
          label: "New Projects",
          action: () => setPage("shop"),
        },
        {
          label: "Fully Funded",
          action: () => setPage("shop"),
        },
      ],
    },

    {
      title: "Resources",
      links: [
        {
          label: "Case Studies",
          action: () => setPage("lookbook"),
        },
        {
          label: "Growing Guide",
          action: () => setPage("advice"),
        },
        {
          label: "Discover More",
          action: () => setPage("shop"),
        },
      ],
    },
  ];

  const iconSize = isMobile ? 20 : 18;

  const navBtnStyle = (key, active = false) => ({
    background: active
      ? T.goldLight
      : hoveredNav === key
      ? "rgba(95,203,140,.08)"
      : "transparent",
    border: "none",
    borderRadius: 999,
    padding: isMobile ? "10px 12px" : "10px 18px",
    fontSize: 13.5,
    color: active ? T.gold : hoveredNav === key ? T.gold : T.ink,
    cursor: "pointer",
    fontWeight: 600,
    letterSpacing: ".01em",
    transition: "all .25s ease",
    display: "flex",
    alignItems: "center",
    gap: 8,
    transform: hoveredNav === key ? "translateY(-2px)" : "translateY(0)",
    boxShadow:
      hoveredNav === key ? "0 8px 20px rgba(95,203,140,.15)" : "none",
    whiteSpace: "nowrap",
  });

  const NavButton = ({ navKey, icon, label, onClick, active }) => (
    <button
      onClick={onClick}
      onMouseEnter={() => setHoveredNav(navKey)}
      onMouseLeave={() => setHoveredNav("")}
      style={navBtnStyle(navKey, active)}
    >
      {icon}
      {!isMobile && <span>{label}</span>}
    </button>
  );

  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 800,
        background: scrolled ? T.paper : "transparent",
        backdropFilter: scrolled ? "blur(20px) saturate(160%)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(20px) saturate(160%)" : "none",
        borderBottom: scrolled ? `1px solid ${T.border}` : "none",
        boxShadow: scrolled ? T.shadowSoft : "none",
        transition: "background .3s ease, box-shadow .3s ease",
      }}
    >
      <div
        style={{
          maxWidth: 1320,
          margin: "0 auto",
          padding: "10px 14px",
          display: "grid",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            alignItems: "center",
            gap: 10,
            minHeight: 46,
          }}
        >
          <div
            style={{ position: "relative", justifySelf: "start" }}
            onMouseEnter={() => !isMobile && setMenuHover(true)}
            onMouseLeave={() => !isMobile && setMenuHover(false)}
          >
            <button
              onClick={() => setMenuOpen((v) => !v)}
              onMouseEnter={() => setCategoryBtnHover(true)}
              onMouseLeave={() => setCategoryBtnHover(false)}
              style={{
                background: showCategoryMenu
                  ? T.ink
                  : T.border,
                border: `1px solid ${showCategoryMenu ? T.ink : T.border}`,
                borderRadius: 999,
                padding: "10px 20px",
                fontSize: 13.5,
                color: showCategoryMenu ? T.white : T.ink,
                fontWeight: 700,
                letterSpacing: ".02em",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                minHeight: 36,
                transition: "all .25s ease",
                transform: categoryBtnHover
                  ? "translateY(-2px)"
                  : "translateY(0)",
                boxShadow: categoryBtnHover ? T.shadowMedium : "none",
              }}
            >
              <Menu size={iconSize} />
              {!isMobile && <span>All Categories</span>}
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  transition: "transform .3s ease",
                  transform: showCategoryMenu
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                }}
              >
                <ChevronDown size={iconSize - 2} />
              </span>
            </button>

            {showCategoryMenu && (
              <div
                style={{
                  position: isMobile ? "static" : "absolute",
                  top: isMobile ? undefined : "calc(100% + 10px)",
                  left: 0,
                  width: isMobile ? "100vw" : 640,
                  maxWidth: isMobile ? "calc(100vw - 20px)" : 640,
                  maxHeight: isMobile ? 300 : 460,
                  background: T.paper,
                  backdropFilter: "blur(18px)",
                  WebkitBackdropFilter: "blur(18px)",
                  border: `1px solid ${T.border}`,
                  borderRadius: 20,
                  boxShadow: T.shadowMedium,
                  zIndex: 1200,
                  marginTop: isMobile ? 8 : 0,
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "290px 1fr",
                  overflow: "hidden",
                  animation: "greenbless-fade-in .25s ease",
                }}
              >
                <div
                  style={{
                    maxHeight: isMobile ? 300 : 460,
                    overflowY: "auto",
                    borderRight: isMobile ? "none" : `1px solid ${T.border}`,
                    background: "transparent",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                    padding: 8,
                    gap: 2,
                  }}
                >
                  <button
                    onClick={() => goCategory("")}
                    onMouseEnter={() => !isMobile && setActiveMenuSlug("")}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      background: currentCategory ? "transparent" : T.goldLight,
                      color: currentCategory ? T.ink : T.gold,
                      border: "none",
                      borderRadius: 12,
                      padding: "12px 14px",
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: ".04em",
                      textTransform: "uppercase",
                      cursor: "pointer",
                      transition: "all .25s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>All Categories</span>
                    <Sparkles size={14} color={T.gold} />
                  </button>

                  {categories.map((cat) => {
                    const isActive =
                      currentCategory === cat.slug || activeMenuSlug === cat.slug;
                    return (
                      <button
                        key={cat.id || cat.slug}
                        onClick={() => goCategory(cat.slug)}
                        onMouseEnter={() =>
                          !isMobile && setActiveMenuSlug(cat.slug)
                        }
                        style={{
                          width: "100%",
                          textAlign: "left",
                          background: isActive ? T.goldLight : "transparent",
                          color: isActive ? T.gold : T.inkSoft,
                          border: "none",
                          borderRadius: 12,
                          padding: "13px 14px",
                          fontSize: 13.5,
                          fontWeight: isActive ? 700 : 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          overflow: "visible",
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          transition: "all .25s ease",
                        }}
                      >
                        <span style={{ flex: 1, overflow: "visible" }}>
                          {cat.name}
                        </span>
                        <ChevronRight
                          size={15}
                          color={isActive ? T.gold : T.muted}
                          style={{
                            marginLeft: 8,
                            flexShrink: 0,
                            transition: "transform .25s ease",
                            transform: isActive
                              ? "translateX(2px)"
                              : "translateX(0)",
                          }}
                        />
                      </button>
                    );
                  })}
                </div>

                {!isMobile && (
                  <div
                    style={{
                      padding: "18px 18px 16px",
                      background: T.paper,
                      display: "grid",
                      gap: 14,
                      maxHeight: 460,
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        letterSpacing: ".2em",
                        textTransform: "uppercase",
                        color: T.gold,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <Sparkles size={12} />
                      Featured Collection
                    </div>
                    {activeCategoryImage && (
                      <img
                        src={activeCategoryImage}
                        alt={activeCategory?.name || "Category"}
                        style={{
                          width: "100%",
                          height: 140,
                          objectFit: "cover",
                          borderRadius: 16,
                          boxShadow: T.shadowMedium,
                        }}
                      />
                    )}
                    <div
                      style={{
                        fontSize: 21,
                        color: T.ink,
                        fontWeight: 800,
                        lineHeight: 1.15,
                        letterSpacing: "-.01em",
                      }}
                    >
                      {activeCategory?.name || "All Categories"}
                    </div>
                    <p
                      style={{
                        margin: 0,
                        color: T.inkSoft,
                        fontSize: 13.5,
                        lineHeight: 1.75,
                        maxWidth: 420,
                      }}
                    >
                      Browse curated products, seasonal picks, and top-rated
                      essentials in this section.
                    </p>
                    <div
                      style={{
                        display: "flex",
                        gap: 12,
                        marginTop: 6,
                      }}
                    >
                      <button
                        onClick={() => goCategory("")}
                        style={{
                          border: `1px solid ${T.border}`,
                          background: T.border,
                          color: T.ink,
                          borderRadius: 999,
                          padding: "11px 20px",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: ".02em",
                          cursor: "pointer",
                          transition: "all .25s ease",
                        }}
                      >
                        View Collection
                      </button>

                      <button
                        onClick={() => setPage("shop")}
                        style={{
                          border: "none",
                          background: T.ink,
                          color: T.white,
                          borderRadius: 999,
                          padding: "11px 20px",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: ".02em",
                          cursor: "pointer",
                          transition: "all .25s ease",
                          boxShadow: T.shadowSoft,
                        }}
                      >
                        Shop Now
                      </button>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 10,
                      }}
                    >
                      {menuFeatureGroups.map((group) => (
                        <div
                          key={group.title}
                          style={{
                            background: T.paper,
                            border: `1px solid ${T.border}`,
                            borderRadius: 16,
                            padding: "18px",
                            boxShadow: T.shadowSoft,
                            transition: "all .3s ease",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 12.5,
                              color: T.ink,
                              fontWeight: 800,
                              marginBottom: 12,
                              letterSpacing: ".05em",
                              textTransform: "uppercase",
                            }}
                          >
                            {group.title}
                          </div>
                          <div style={{ display: "grid", gap: 9 }}>
                            {group.links.map((link) => (
                              <button
                                key={link.label}
                                onClick={link.action}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  textAlign: "left",
                                  color: T.inkSoft,
                                  fontSize: 12.5,
                                  fontWeight: 500,
                                  padding: 0,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  transition: "color .25s ease",
                                }}
                              >
                                <ChevronRight size={12} color={T.gold} />
                                {link.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Brand Logo */}

          <img
            src={cropbankLogo}
            alt="CropBank"
            style={{
              justifySelf: "center",
              height: isMobile ? 72 : 110,
              cursor: "pointer",
              userSelect: "none",
              filter: theme === "light" ? "invert(1) brightness(0.85)" : "none",
            }}
            onClick={() => setPage("home")}
          />

          {/* Actions */}
          <div
            style={{
              display: "flex",
              gap: 4,
              alignItems: "center",
              justifySelf: "end",
              flexWrap: isMobile ? "nowrap" : "wrap",
            }}
          >
            {!user ? (
              <>
              <NavButton
                  navKey="explore"
                  icon={<Compass size={iconSize} />}
                  label="Explore"
                  onClick={() => setPage("explore")}
                />
                <NavButton
                  navKey="login"
                  icon={<LogIn size={iconSize} />}
                  label="Sign In"
                  onClick={() => setPage("login")}
                />
              
                
              </>
            ) : user.role === "farmer" ? (
              <>
                <NavButton
                  navKey="dashboard"
                  icon={<LayoutDashboard size={iconSize} />}
                  label="My Farm"
                  onClick={() => navigate("/farmer")}
                />
                <NavButton
                  navKey="market"
                  icon={<ShoppingBag size={iconSize} />}
                  label="Market"
                  onClick={() => navigate("/market")}
                />
                <NavButton
                  navKey="portfolio"
                  icon={<Package size={iconSize} />}
                  label="Portfolio"
                  onClick={() => navigate("/portfolio")}
                />
                <NavButton
                  navKey="account"
                  icon={<UserRound size={iconSize} />}
                  label="Account"
                  onClick={() => setPage("profile")}
                />
                <NavButton
                  navKey="logout"
                  icon={<LogOut size={iconSize} />}
                  label="Logout"
                  onClick={logout}
                />
              </>
            ) : user.role === "admin" || user.role === "superadmin" ? (
              <>
                <NavButton
                  navKey="dashboard"
                  icon={<LayoutDashboard size={iconSize} />}
                  label="Dashboard"
                  onClick={() => navigate("/admin")}
                />
                <NavButton
                  navKey="orders"
                  icon={<Package size={iconSize} />}
                  label="Orders"
                  onClick={() => navigate("/admin/orders")}
                />
                <NavButton
                  navKey="security"
                  icon={<ShieldCheck size={iconSize} />}
                  label="Security"
                  onClick={() => navigate("/admin/security")}
                />
                {user.role === "superadmin" && (
                  <NavButton
                    navKey="manage-admins"
                    icon={<UserCog size={iconSize} />}
                    label="Admins"
                    onClick={() => navigate("/admin/manage")}
                  />
                )}
                <NavButton
                  navKey="account"
                  icon={<UserRound size={iconSize} />}
                  label="Account"
                  onClick={() => navigate("/admin/profile")}
                />
                <NavButton
                  navKey="logout"
                  icon={<LogOut size={iconSize} />}
                  label="Logout"
                  onClick={logout}
                />
              </>
            ) : (
              <>
                <NavButton
                  navKey="home"
                  icon={<House size={iconSize} />}
                  label="Home"
                  onClick={() => setPage("home")}
                />
                <NavButton
                  navKey="shop"
                  icon={<ShoppingBag size={iconSize} />}
                  label="Projects"
                  onClick={() => setPage("shop")}
                />
                <NavButton
                  navKey="explore"
                  icon={<Compass size={iconSize} />}
                  label="Explore"
                  onClick={() => setPage("explore")}
                />
                <NavButton
                  navKey="market"
                  icon={<ShoppingBag size={iconSize} />}
                  label="Market"
                  onClick={() => navigate("/market")}
                />
                <NavButton
                  navKey="portfolio"
                  icon={<Package size={iconSize} />}
                  label="Portfolio"
                  onClick={() => navigate("/portfolio")}
                />

                {/* <button
        onClick={() => setPage("orders")}
        style={navBtn}
      >
         Orders
      </button> */}

                <NavButton
                  navKey="account"
                  icon={<UserRound size={iconSize} />}
                  label="Account"
                  onClick={() => setPage("profile")}
                />
              </>
            )}

            {/* <button
  onClick={() => setPage("search")}
  style={{
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: T.ink,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
  <FiSearch size={20} />
</button> */}

            <button
              onClick={toggleTheme}
              onMouseEnter={() => setThemeHover(true)}
              onMouseLeave={() => setThemeHover(false)}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              style={{
                background: themeHover ? T.goldLight : "transparent",
                border: `1px solid ${T.border}`,
                borderRadius: 999,
                width: isMobile ? 38 : 42,
                height: isMobile ? 38 : 42,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme === "dark" ? T.gold : T.ink,
                transition: "all .25s ease",
                transform: themeHover ? "translateY(-2px)" : "translateY(0)",
              }}
            >
              {theme === "dark" ? <Sun size={iconSize} /> : <Moon size={iconSize} />}
            </button>

            <button
              onClick={() => setOpen(true)}
              onMouseEnter={() => setCartHover(true)}
              onMouseLeave={() => setCartHover(false)}
              style={{
                background: T.ink,
                color: T.white,
                border: "none",
                borderRadius: 999,
                padding: isMobile ? "9px 12px" : "9px 18px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginLeft: 6,
                transition: "all .25s ease",
                transform: cartHover ? "translateY(-2px)" : "translateY(0)",
                boxShadow: cartHover
                  ? T.shadowMedium
                  : T.shadowSoft,
              }}
            >
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShoppingBag size={isMobile ? 20 : 19} />

                {count > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: -9,
                      right: -9,
                      minWidth: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: T.gold,
                      color: T.paper,
                      fontSize: 10,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 4px",
                      lineHeight: 1,
                      boxShadow: "0 0 0 2px " + T.ink,
                    }}
                  >
                    {count}
                  </span>
                )}
              </div>

              {!isMobile && <span>Cart</span>}
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes greenbless-fade-in {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </nav>
  );
}